/**
 * Constellation Twin React Query hooks (P83.2).
 *
 * Mirrors:
 *   GET /v1/twin/graph                                  -> useTwinGraph
 *   GET /v1/twin/blast-graph/{kind}/{id}                -> useTwinBlast
 *   GET /v1/twin/blast-graph/{kind}/{id}/history        -> useTwinBlastHistory
 *   GET /v1/twin/provenance/{chain}/{kind}/{id}         -> useProvenance
 *   POST /v1/admin/chains/{slug}/verify                 -> useVerifyChain
 *
 * Kept in a sibling file to twin/api/hooks.ts so the cascade /
 * capmap / landscape / chain DIFF hooks don't bloat. Same module-
 * boundary rules apply: imports only from twin/ + shared/.
 */
import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { provenanceKeys, twinBlastKeys, twinGraphKeys } from "shared/api/queryClient";
import type {
  ProvenanceTrace,
  TwinBlastGraph,
  TwinBlastHistory,
  TwinGraph,
  TwinNodeKind,
} from "shared/api/types";

/**
 * Verify-chain response, mirroring `ChainVerifyOut` in
 * backend/twin/chain_verify_router.py.
 *
 * This previously declared `{ intact, walked, broken_at }`, which the
 * backend has never returned -- the two shapes shared NO fields. Every
 * key read back `undefined`, so a clean chain rendered "Chain broken at
 * snapshot #?" and the flagship provenance surface reported a break on
 * healthy data. Kept in step by `test_chain_verify_contract.py`, which
 * names this interface.
 */
export interface ChainBreak {
  offset: number;
  entity_kind: string;
  entity_id: string;
  row_id: string;
  kind: string;
  detail: string;
}

export interface VerifyChainResponse {
  chain: string;
  table_name: string;
  rows_examined: number;
  rows_valid: number;
  is_clean: boolean;
  breaks: ChainBreak[];
  /** Round 16 C15 -- seals that link correctly but break the chain's own
   *  rule (an approval sealed twice). Not tamper; `is_clean` ignores them. */
  defects?: ChainBreak[];
}

/**
 * Full topology read for the constellation page.
 *
 * `as_of` is plumbed for forward compatibility (the BLAST_RADIUS
 * deriver story replays past states when it ships). Today the
 * backend returns current-state regardless of the value, but the
 * query key still distinguishes so the scrubber's tick selection
 * doesn't reuse a stale cache when as_of changes.
 */
export function useTwinGraph(
  opts: { as_of?: string | null } = {},
): UseQueryResult<TwinGraph, ApiError> {
  const asOf = opts.as_of ?? null;
  return useQuery({
    queryKey: twinGraphKeys.query({ as_of: asOf }),
    queryFn: () =>
      api.get<TwinGraph>(
        asOf ? `/v1/twin/graph?as_of=${encodeURIComponent(asOf)}` : "/v1/twin/graph",
      ),
  });
}

/**
 * Counterfactual blast from a seed node (server-side BFS + chain
 * seal lazy-genesis). Enabled only when a node is selected.
 */
export function useTwinBlast(opts: {
  kind: TwinNodeKind | null;
  id: string | null;
  as_of?: string | null;
}): UseQueryResult<TwinBlastGraph, ApiError> {
  const { kind, id } = opts;
  const asOf = opts.as_of ?? null;
  return useQuery({
    queryKey: twinBlastKeys.query({
      kind: kind ?? "_none",
      id: id ?? "_none",
      as_of: asOf,
    }),
    queryFn: () => {
      const base = `/v1/twin/blast-graph/${kind}/${id}`;
      const url = asOf ? `${base}?as_of=${encodeURIComponent(asOf)}` : base;
      return api.get<TwinBlastGraph>(url);
    },
    enabled: Boolean(kind && id),
  });
}

/**
 * BLAST_RADIUS chain snapshot history for one entity (newest first).
 * Drives the time-travel scrubber's tick list. Limit clamped to
 * [1, 100] at the API layer; this hook passes whatever the caller
 * supplies and lets the 422 surface to the React Query error state.
 */
export function useTwinBlastHistory(opts: {
  kind: TwinNodeKind | null;
  id: string | null;
  limit?: number;
}): UseQueryResult<TwinBlastHistory, ApiError> {
  const { kind, id } = opts;
  const limit = opts.limit ?? 10;
  return useQuery({
    queryKey: twinBlastKeys.history({
      kind: kind ?? "_none",
      id: id ?? "_none",
      limit,
    }),
    queryFn: () =>
      api.get<TwinBlastHistory>(
        `/v1/twin/blast-graph/${kind}/${id}/history?limit=${limit}`,
      ),
    enabled: Boolean(kind && id),
  });
}

/**
 * Provenance trace for one (chain, entity) snapshot. Opens via the
 * "Show how this was derived" buttons in the inspector + metrics
 * rail + state pill. 404 surfaces when no snapshot exists for the
 * coordinates (lazy-genesis territory).
 */
export function useProvenance(opts: {
  chain: string | null;
  entity_kind: string | null;
  entity_id: string | null;
  as_of?: string | null;
}): UseQueryResult<ProvenanceTrace, ApiError> {
  const { chain, entity_kind, entity_id } = opts;
  const asOf = opts.as_of ?? null;
  return useQuery({
    queryKey: provenanceKeys.query({
      chain: chain ?? "_none",
      entity_kind: entity_kind ?? "_none",
      entity_id: entity_id ?? "_none",
      as_of: asOf,
    }),
    queryFn: () => {
      const base = `/v1/twin/provenance/${chain}/${entity_kind}/${entity_id}`;
      const url = asOf ? `${base}?as_of=${encodeURIComponent(asOf)}` : base;
      return api.get<ProvenanceTrace>(url);
    },
    enabled: Boolean(chain && entity_kind && entity_id),
  });
}

/**
 * Re-walk the chain server-side. Admin-only at the endpoint layer;
 * the SPA's Verify button surfaces a forbidden notice when the
 * mutation 403s.
 */
export function useVerifyChain() {
  return useMutation<VerifyChainResponse, ApiError, { slug: string }>({
    mutationFn: ({ slug }) =>
      api.post<VerifyChainResponse>(`/v1/admin/chains/${slug}/verify`, {}),
  });
}
